// TSConnection.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MSGTYPE_HEARTBEAT, MSGTYPE_PARTICIPANT_REPLY, MSGTYPE_TS_LOGON } from '../../common/msg_types';
import { ADD_ROW, EVENT_TOPIC_FOR_TRANSACTION_SERVER, GetCurrentTimeString, GetOrCreateBrowserSessionId, JSON_KEY_BROWSER_SESSION_ID, JSON_KEY_IN_SEQ, JSON_KEY_MESSAGE, JSON_KEY_MESSAGE_TYPE, JSON_KEY_RESULTS_TYPE, JSON_KEY_SUBMITTER, JSON_KEY_TEST_ID, JSON_KEY_TIME, MESSAGES_TABLE, MESSAGES_TYPE_SENT_MESSAGE } from '../../common/common';
import { incrementSeqNum, selectInstrumentsLoaded, selectSeqNum, selectTSUserId, setInstrumentsLoaded, setTSConnected } from '../../redux/globalsSlice';
import { DispatchTableEvent } from '../events/events';
import { ProcessMessage } from './process_messages';
import { selectIsMarketController } from '../../redux/globalsSlice';

const HEARTBEAT_INTERVAL = 20000; // 20 seconds

// Outside the component
let wsExternalRef = null;

export const closeTSConnection = () => {
   if (wsExternalRef && wsExternalRef.readyState === WebSocket.OPEN) {
      console.log('[TSConnection] Closing WebSocket externally...');
      wsExternalRef.close();
   }
};

const socketUrl = window.location.port === "5173"
    ? window.APP_CONFIG?.TRANSACTION_URL || `ws://${window.location.hostname}:9401`
    : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/ws/`;

export const TSConnection1 = () => {

   // console.log(`[TSConnection #${mountId.current}] Component mounted/rendered`);

   // // Track state changes
   // useEffect(() => {
   //    console.log(`[TSConnection #${mountId.current}] isCollectingInstruments:`, isCollectingInstruments.current);
   //    console.log(`[TSConnection #${mountId.current}] isReadyToProcess:`, isReadyToProcess.current);
   //    console.log(`[TSConnection #${mountId.current}] isInstrumentsLoaded:`, isInstrumentsLoaded);
   // });

   const dispatch = useDispatch();
   const isInstrumentsLoaded = useSelector(selectInstrumentsLoaded);

   // console.log("url : ", socketUrl);
   
   const [loggedOn, setLoggedOn] = useState(false); 
   const [connectionStatus, setConnectionStatus] = useState('Closed');
   const wsRef = useRef(null);
   const messageQueue = useRef([]);
   const messageBufferQueue = useRef([]);
   const isCollectingInstruments = useRef(true);
   const isReadyToProcess = useRef(false);
   const isMarketController = useSelector(selectIsMarketController);
   const isMarketControllerRef = useRef(isMarketController);

   // to have the latest tsuserid
   const tsUserId = useSelector(selectTSUserId);
   const tsUserIdRef = useRef(tsUserId);
   useEffect(() => {
      tsUserIdRef.current = tsUserId;
   }, [tsUserId]);


   useEffect(() => {
      isMarketControllerRef.current = isMarketController;
   }, [isMarketController]);
   
   // to always have the latest sequence number
   const inSeqNum = useSelector(selectSeqNum);
   const inSeqNumRef = useRef(inSeqNum);
   useEffect(() => {
      inSeqNumRef.current = inSeqNum;
   }, [inSeqNum]);
   
   // -- Send logic --
   const sendMessage = useCallback((msgObj) => {
      const message = JSON.stringify(msgObj);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
         // console.log('Sending Message: ', message);
         wsRef.current.send(message);
      } else {
         messageQueue.current.push(message);
      }
   }, []);

   const PrepareToSendMessage = useCallback((message) => {
      message[JSON_KEY_BROWSER_SESSION_ID] = GetOrCreateBrowserSessionId(); // always add browser session id

      if (message[JSON_KEY_MESSAGE_TYPE] !== MSGTYPE_TS_LOGON) {
            message[JSON_KEY_SUBMITTER] = tsUserIdRef.current;
            message[JSON_KEY_IN_SEQ] = inSeqNumRef.current;
      }
   
      dispatch(incrementSeqNum());
   
      const newMessage = {
            [JSON_KEY_RESULTS_TYPE]: MESSAGES_TYPE_SENT_MESSAGE,
            [JSON_KEY_MESSAGE]: message,
            [JSON_KEY_TIME]: GetCurrentTimeString(),
      };
   
      sendMessage(message); // <-- message to WebSocket, raw
      DispatchTableEvent(ADD_ROW, MESSAGES_TABLE, newMessage); // <-- newMessage to table
      // console.log('TS Send Message: ', JSON.stringify(message));
   }, [dispatch, sendMessage]);

   useEffect(() => {
      const handleEvent = (event) => {
            const { data } = event.detail;
            PrepareToSendMessage(data);
      };
   
      window.addEventListener(EVENT_TOPIC_FOR_TRANSACTION_SERVER, handleEvent);

      return () => {
         window.removeEventListener(EVENT_TOPIC_FOR_TRANSACTION_SERVER, handleEvent);
      };
   }, [PrepareToSendMessage]);
   
   // -- Main message processor (delayed until instruments ready) --
   const handleMessage = useCallback((event) => {
      const message = JSON.parse(event.data);
      // console.log('[TSConnection] Received message:', message);
      // console.log(`[TSConnection #${mountId.current}] Received:`, message.m_type, 'collecting:', isCollectingInstruments.current);

      // if (message.m_type === MSGTYPE_PARTICIPANT_REPLY) {
      //    console.warn(`[TSConnection #${mountId.current}] PARTICIPANT_REPLY received - this might clear data!`);
      // }

      // Step 1: Check if still collecting instruments
      if (isCollectingInstruments.current) {
         // console.log('isMarketControllerRef.current ', isMarketControllerRef.current );
         ProcessMessage(message, dispatch, setLoggedOn, tsUserIdRef.current, isMarketControllerRef.current);
         if (message.m_type === MSGTYPE_PARTICIPANT_REPLY) {
            // console.log('tsconnection1, message: ', event.data);
            isCollectingInstruments.current = false;

            setTimeout(() => {
               // console.log('tsconnection1, dispatching setInstrumentLoaded to true');
               dispatch(setInstrumentsLoaded(true));
            }, 2000);
            return;
         }
         return; 
      } 

      // Step 2: Instruments done, but Redux not updated yet
      if (!isReadyToProcess.current) {
         messageBufferQueue.current.push(message);
         return;
      }

      // Step 3: Ready to go
      // console.log('isMarketControllerRef.current ', isMarketControllerRef.current );
      ProcessMessage(message, dispatch, setLoggedOn, tsUserIdRef.current, isMarketControllerRef.current);
   }, [dispatch]);

   // -- Watch for isInstrumentsLoaded from Redux --
   useEffect(() => {
      if (!isCollectingInstruments.current && isInstrumentsLoaded) {
         // console.log('[TSConnection] Redux instruments loaded. Releasing buffer...');
         isReadyToProcess.current = true;
         // messageBufferQueue.current.forEach(ProcessMessage);
         messageBufferQueue.current.forEach(msg => ProcessMessage(msg, dispatch, setLoggedOn, tsUserIdRef.current));
         messageBufferQueue.current = [];
      }
   }, [isInstrumentsLoaded, dispatch]);

   // -- WebSocket connect logic --
   useEffect(() => {
      const ws = new WebSocket(socketUrl);
      wsRef.current = ws;
      wsExternalRef = ws;

      ws.onopen = () => {
         setConnectionStatus('Open');
         dispatch(setTSConnected(true));

         while (messageQueue.current.length > 0) {
            ws.send(messageQueue.current.shift());
         }
      };

      ws.onmessage = handleMessage;

      ws.onclose = () => {
         setConnectionStatus('Closed');
         dispatch(setTSConnected(false));
         isReadyToProcess.current = false;
      };

      const heartbeatInterval = setInterval(() => {
         // console.log('[TSConnection] sending heartbeat ');
         // console.log(`[TSConnection] sending heartbeat at ${new Date().toLocaleTimeString()}`);
         sendMessage({
            [JSON_KEY_MESSAGE_TYPE]: MSGTYPE_HEARTBEAT,
            [JSON_KEY_TEST_ID]: 'ABCDE',
         });
      }, HEARTBEAT_INTERVAL);

      ws.onerror = (err) => {
         console.error('[TSConnection] WebSocket error', err);
      };

      return () => {
         ws.close();
         wsExternalRef = null;
         clearInterval(heartbeatInterval);
      };
   }, [dispatch, handleMessage, sendMessage]);

   return ([connectionStatus, loggedOn, setLoggedOn]);
};

